import {
  ref,
  uploadBytes,
  getDownloadURL,
  deleteObject,
  type UploadMetadata,
} from 'firebase/storage';
import { storage } from '@/config/firebase';

export async function uploadFile(
  path: string,
  file: File | Blob,
  metadata?: UploadMetadata
): Promise<string> {
  const storageRef = ref(storage, path);
  await uploadBytes(storageRef, file, metadata);
  return getDownloadURL(storageRef);
}

/**
 * Upload an attachment to the temporary `t/` path for sending via Twilio.
 * Twilio fetches the file from this URL, so it only needs to exist briefly.
 * Returns { url, path } so the caller can delete after send.
 */
export async function uploadAttachment(file: File): Promise<{ url: string; path: string }> {
  const ext = file.name.includes('.') ? file.name.substring(file.name.lastIndexOf('.')) : '';
  const baseName = file.name.includes('.') ? file.name.substring(0, file.name.lastIndexOf('.')) : file.name;
  const uniqFileName = `${baseName}_${Date.now()}${ext}`;
  const path = `t/${uniqFileName}`;
  const url = await uploadFile(path, file, {
    contentType: file.type,
    contentDisposition: `inline; filename="${file.name}"`,
  });
  return { url, path };
}

/**
 * Upload an attachment for an internal note to a permanent per-user path — a
 * note's media is referenced from the Firestore note doc and re-rendered every
 * time the thread is opened, so conceptually it doesn't belong in the `t/` temp
 * prefix that `uploadAttachment` uses.
 *
 * NOT IN USE YET: the deployed bucket rules only permit known prefixes and
 * reject `inbox-notes/**` with storage/unauthorized (403). Neither repo deploys
 * storage.rules — they're console-managed. Once the prefix is allowed, switch
 * the note branch in chat-input.tsx back to this helper.
 */
export async function uploadNoteAttachment(
  file: File,
  uid: string,
): Promise<{ url: string; path: string }> {
  const safeName = file.name.replace(/[^a-zA-Z0-9._-]/g, '_');
  const path = `inbox-notes/${uid}/${Date.now()}_${safeName}`;
  const url = await uploadFile(path, file, {
    contentType: file.type,
    contentDisposition: `inline; filename="${file.name}"`,
  });
  return { url, path };
}

export async function getFileURL(path: string): Promise<string> {
  const storageRef = ref(storage, path);
  return getDownloadURL(storageRef);
}

export async function deleteFile(path: string): Promise<void> {
  const storageRef = ref(storage, path);
  await deleteObject(storageRef);
}
